import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button, ConfirmDialog, NotificationBanner } from '../../../components/idsk';
import { mergeDefinition, type FieldDef, type FormDefinition } from '../../../form-engine';
import { text } from '../../../form-engine/types';
import { useAnnouncer } from '../../../layout/Announcer';
import { toast } from '../../../components/common/toasts';
import { canMoveToSection, dependentsOf, locate, moveCustomField, moveField, newOrderErrors, removeField, validateFor } from './definitionOps';
import type { BuilderMode } from './FieldEditor';
import { isGroup } from './palette';

type Change = { definition: FormDefinition; customFields: FieldDef[] };

interface FieldProps {
  mode: BuilderMode;
  /** template mode: the draft; custom mode: the pinned template definition */
  definition: FormDefinition;
  customFields: FieldDef[];
  fieldKey: string;
}

export interface FieldLinksProps extends FieldProps {
  /** route of the palette page adding a field into a group */
  newPath: (parentKey: string) => string;
  /** route of the "Presunúť do sekcie" page; no such action without it */
  movePath?: (key: string) => string;
}

export interface FieldActionsProps extends FieldProps {
  /** Persists a moved or removed field at once; false when it was not saved (the page shows why). */
  onChange: (next: Change) => Promise<boolean>;
  /** After the field was removed, with the message to show (the pane closes; a toast says it). */
  onRemoved: (message: string) => void;
}

/**
 * The buttons of the selected field in the always visible action bar of the properties pane, saved at once:
 * "Posunúť vyššie / nižšie" (the focus stays on the button and a toast tells the new position) and "Odstrániť".
 * A move that would put a field above a field its rule depends on (or below a field depending on it), and removing a
 * field other rules depend on, are refused with an explanation. With FieldLinks they are the way to do without
 * dragging (WCAG 2.5.7).
 */
export function FieldActions({ mode, definition, customFields, fieldKey, onChange, onRemoved }: FieldActionsProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage === 'en' ? 'en' : 'sk';
  const { announce, alert } = useAnnouncer();
  const [problem, setProblem] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const effective = mode === 'template' ? definition : mergeDefinition(definition, customFields);
  const loc = locate(effective.fields, fieldKey);
  if (!loc) return null;
  const f = loc.field;
  const label = (x: FieldDef) => text(x.label, lang);

  const block = (message: string) => {
    setProblem(message);
    alert(message);
  };

  const move = async (direction: -1 | 1) => {
    setProblem(null);
    let next: Change | null = null;
    if (mode === 'template') {
      const moved = moveField(definition, f.key, direction);
      if (moved) next = { definition: moved, customFields };
    } else if (locate(customFields, f.key)?.parent) {
      // a field of the team's own group moves within the group
      const moved = moveField({ fields: customFields }, f.key, direction);
      if (moved) next = { definition, customFields: moved.fields };
    } else {
      const moved = moveCustomField(definition, customFields, f.key, direction);
      if (moved) next = { definition, customFields: moved };
    }
    if (!next) {
      announce(direction < 0 ? t('builder.list.alreadyFirst', { label: label(f) }) : t('builder.list.alreadyLast', { label: label(f) }));
      return;
    }
    const nextEffective = mode === 'template' ? next.definition : mergeDefinition(next.definition, next.customFields);
    const introduced = newOrderErrors(validateFor(mode, effective, definition, customFields), validateFor(mode, nextEffective, next.definition, next.customFields));
    if (introduced.length) {
      const dependent = locate(nextEffective.fields, introduced[0]!.field)?.field;
      const neighbour = loc.siblings[loc.index + direction];
      const other = dependent && dependent.key === f.key ? neighbour : f;
      block(t('builder.list.moveBlocked', { dependent: dependent ? label(dependent) : introduced[0]!.field, dependency: other ? label(other) : '' }));
      return;
    }
    if (!(await onChange(next))) return;
    const moved = locate(nextEffective.fields, f.key);
    toast.success(t('builder.list.moved', { label: label(f), position: (moved?.index ?? 0) + 1, count: moved?.siblings.length ?? 0 }));
    requestAnimationFrame(() => document.getElementById(`${direction < 0 ? 'move-up' : 'move-down'}-${f.key}`)?.focus());
  };

  const remove = async () => {
    setDeleting(false);
    const dependents = dependentsOf(effective, f.key).filter((d) => d.key !== f.key);
    if (dependents.length) {
      block(t('builder.list.deleteBlocked', { label: label(f), dependents: dependents.map(label).join(', ') }));
      return;
    }
    const next =
      mode === 'template'
        ? { definition: removeField(definition, f.key), customFields }
        : {
            definition,
            customFields: removeField({ fields: customFields }, f.key).fields.map((c) => (c.placeAfter === f.key ? { ...c, placeAfter: f.placeAfter ?? null } : c)),
          };
    if (!(await onChange(next))) return;
    onRemoved(t('builder.list.deleted', { label: label(f) }));
  };

  return (
    <div className="app-field-actions">
      {problem && (
        <NotificationBanner type="error" title={t('builder.list.problem')}>
          {problem}
        </NotificationBanner>
      )}
      <div className="app-properties__buttons">
        <Button id={`move-up-${f.key}`} variant="secondary" onClick={() => void move(-1)}>
          {t('builder.list.moveUp')} <span className="govuk-visually-hidden">{label(f)}</span>
        </Button>
        <Button id={`move-down-${f.key}`} variant="secondary" onClick={() => void move(1)}>
          {t('builder.list.moveDown')} <span className="govuk-visually-hidden">{label(f)}</span>
        </Button>
        <Button variant="warning" onClick={() => setDeleting(true)}>
          {t('common.delete')} <span className="govuk-visually-hidden">{label(f)}</span>
        </Button>
      </div>
      <ConfirmDialog
        open={deleting}
        title={t('builder.list.confirmDelete.title')}
        confirmText={t('builder.list.confirmDelete.confirm')}
        danger
        onConfirm={() => void remove()}
        onCancel={() => setDeleting(false)}
      >
        <p className="govuk-body">{t('builder.list.confirmDelete.body', { label: label(f) })}</p>
      </ConfirmDialog>
    </div>
  );
}

/** "Pridať pole do skupiny" and "Presunúť do sekcie" of the selected field, at the top of the properties pane. */
export function FieldLinks({ mode, definition, customFields, fieldKey, newPath, movePath }: FieldLinksProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage === 'en' ? 'en' : 'sk';
  const own = mode === 'template' ? definition.fields : customFields;
  const f = locate(mode === 'template' ? definition.fields : mergeDefinition(definition, customFields).fields, fieldKey)?.field;
  if (!f) return null;
  const label = text(f.label, lang);
  // a field can move into a section (or out of one) when there is somewhere to go
  const movable = canMoveToSection({ fields: own }, f.key) && (Boolean(locate(own, f.key)?.parent) || own.some((s) => s.type === 'SECTION'));
  if (!isGroup(f) && !(movePath && movable)) return null;
  return (
    <ul className="govuk-list app-field-links">
      {isGroup(f) && (
        <li>
          <Link className="govuk-link" to={newPath(f.key)}>
            {t('builder.list.addToGroup')} <span className="govuk-visually-hidden">{label}</span>
          </Link>
        </li>
      )}
      {movePath && movable && (
        <li>
          <Link className="govuk-link" to={movePath(f.key)}>
            {t('builder.list.moveToSection')} <span className="govuk-visually-hidden">{label}</span>
          </Link>
        </li>
      )}
    </ul>
  );
}
